import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';

type OrganizationItem = {
  id: string;
  name: string;
  type: 'BRAND' | 'AGENCY';
  createdAt: string;
  metrics: {
    memberCount: number;
    jobCount: number;
    totalBudgetRM: number;
  };
};

type OrganizationsResponse = {
  items: OrganizationItem[];
  total: number;
};

export default function AdminBrands() {
  const [items, setItems] = useState<OrganizationItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get<OrganizationsResponse>('/admin/organizations', {
          params: { type: 'BRAND', search: search || undefined },
        });
        if (!active) return;
        setItems(data.items || []);
      } catch (err: any) {
        if (!active) return;
        setError(err.response?.data?.error || 'Failed to load brands');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [search]);

  const stats = useMemo(() => {
    const totalBudget = items.reduce((sum, i) => sum + (i.metrics?.totalBudgetRM || 0), 0);
    const totalJobs = items.reduce((sum, i) => sum + (i.metrics?.jobCount || 0), 0);
    const totalMembers = items.reduce((sum, i) => sum + (i.metrics?.memberCount || 0), 0);
    return {
      total: items.length,
      totalBudget,
      totalJobs,
      totalMembers,
    };
  }, [items]);

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">Brand Management</h1>
        <p className="text-glass-text-2 text-sm mt-1">All brand organizations across the platform</p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total brands</div>
          <div className="stat-display text-2xl mt-1">{stats.total}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total Members</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.totalMembers}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total Campaigns</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{stats.totalJobs}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Budget (RM)</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.totalBudget.toLocaleString()}</div>
        </div>
      </div>

      <div className="glass-card p-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brand name"
          className="glass-input w-full px-4 py-2"
        />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Members</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Campaigns</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Budget (RM)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-glass-text-2">
                    Loading brands...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-glass-text-2">
                    No brands found.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-glass-3/40 transition-colors">
                    <td className="px-4 py-4">
                      <div className="font-medium text-glass-text">{item.name}</div>
                      <div className="text-xs text-glass-text-3">{item.id}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{item.metrics?.memberCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{item.metrics?.jobCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-accent2">
                      {(item.metrics?.totalBudgetRM ?? 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
